import React from 'react';
import { Check, Xmark } from 'iconoir-react';
import { SubscriptionPlan } from '../../services/api';
import { getPlanPricing, getPlanOverageRates, toDisplayNumber, formatStorageMb } from '../../utils/planPricing';
import '../../styles/pricing-compare.css';

interface PricingComparisonTableProps {
  plans: SubscriptionPlan[];
  ctaLabel: (plan: SubscriptionPlan) => string;
  onChoosePlan: (plan: SubscriptionPlan) => void;
}

interface ComparisonRow {
  label: string;
  values: React.ReactNode[];
}

const quotaOrUnlimited = (value: unknown): React.ReactNode => (value == null ? 'Illimité' : toDisplayNumber(value));

const buildRows = (plans: SubscriptionPlan[]): ComparisonRow[] => [
  {
    label: 'Prix',
    values: plans.map((plan) => {
      const pricing = getPlanPricing(plan);
      return `${pricing.amount.toFixed(2)}€${pricing.suffix ? ` ${pricing.suffix}` : ''}`;
    }),
  },
  { label: 'Tableaux', values: plans.map((plan) => quotaOrUnlimited(plan.max_boards)) },
  { label: 'Colonnes / tableau', values: plans.map((plan) => quotaOrUnlimited(plan.max_columns_per_board)) },
  { label: 'Cartes', values: plans.map((plan) => quotaOrUnlimited(plan.max_cards_total)) },
  { label: 'Invités / tableau', values: plans.map((plan) => quotaOrUnlimited(plan.max_guests_per_board)) },
  {
    label: 'Stockage',
    values: plans.map((plan) =>
      plan.max_storage_mb == null ? 'Illimité' : formatStorageMb(toDisplayNumber(plan.max_storage_mb))
    ),
  },
  {
    label: 'Publicités',
    values: plans.map((plan) =>
      plan.has_ads ? (
        <span className="pricing-compare-flag pricing-compare-flag-warn">
          <Xmark width={14} height={14} strokeWidth={2.5} /> Affichées
        </span>
      ) : (
        <span className="pricing-compare-flag pricing-compare-flag-ok">
          <Check width={14} height={14} strokeWidth={2.5} /> Aucune
        </span>
      )
    ),
  },
  {
    label: 'Dépassements',
    values: plans.map((plan) => {
      if (plan.has_ads) return <span className="pricing-compare-muted">Non disponibles</span>;
      const rates = getPlanOverageRates(plan);
      return (
        <ul className="pricing-compare-overage-list">
          <li>{rates.boardsTrancheSize} tableaux : {rates.boardsPricePerTranche.toFixed(2)}€ {rates.periodSuffix}</li>
          <li>{rates.cardsTrancheSize} cartes : {rates.cardsPricePerTranche.toFixed(2)}€ {rates.periodSuffix}</li>
          <li>1 Go : {rates.storagePricePerGb.toFixed(2)}€ {rates.periodSuffix}</li>
        </ul>
      );
    }),
  },
];

export const PricingComparisonTable: React.FC<PricingComparisonTableProps> = ({ plans, ctaLabel, onChoosePlan }) => {
  const rows = buildRows(plans);

  return (
    <div className="pricing-compare">
      <table className="pricing-compare-table">
        <thead>
          <tr>
            <th scope="col" className="pricing-compare-row-label" />
            {plans.map((plan) => (
              <th scope="col" key={plan.id}>
                <span className="pricing-compare-plan-name">{plan.display_name}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.label}>
              <th scope="row" className="pricing-compare-row-label">{row.label}</th>
              {row.values.map((value, index) => (
                <td key={plans[index].id}>{value}</td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td className="pricing-compare-row-label" />
            {plans.map((plan) => (
              <td key={plan.id}>
                <button type="button" className="pricing-compare-cta" onClick={() => onChoosePlan(plan)}>
                  {ctaLabel(plan)}
                </button>
              </td>
            ))}
          </tr>
        </tfoot>
      </table>

      <div className="pricing-compare-mobile">
        {plans.map((plan, planIndex) => (
          <div key={plan.id} className="pricing-compare-mobile-card">
            <h3>{plan.display_name}</h3>
            <dl>
              {rows.map((row) => (
                <div className="pricing-compare-mobile-row" key={row.label}>
                  <dt>{row.label}</dt>
                  <dd>{row.values[planIndex]}</dd>
                </div>
              ))}
            </dl>
            <button type="button" className="pricing-compare-cta" onClick={() => onChoosePlan(plan)}>
              {ctaLabel(plan)}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
